<div class="sidebar-process-steps">
  @for (stage of mainStages; track stage.id; let idx = $index) {
    <ng-container *ngTemplateOutlet="stageBlock; context: { $implicit: stage, idx: idx }"></ng-container>
  }

  @if (isArtifactViewAvailable) {
    <mat-divider></mat-divider>
    <div class="workplan-button" (click)="onSwitchToArtifact()">
      @if (isGeneratingArtifact) {
        <mat-spinner [diameter]="20"></mat-spinner>
      } @else {
        <mat-icon class="workplan-icon">{{ isPresentingArtifactView ? 'chat_bubble' : 'assignment' }}</mat-icon>
      }
      <span class="workplan-label">
        {{ isPresentingArtifactView ? ('structuredProcessView.chatView' | translate) : ('structuredProcessView.artifactView' | translate) }}
      </span>
    </div>
  }

  @if (readyToGoStep; as step) {
    <div class="stage-header"
      [class.active]="step.id === currentStepId && !isOnPersonalView"
      [class.completed]="step.status === statusEnum.COMPLETED"
      [class.in-progress]="step.status === statusEnum.IN_PROGRESS"
      (click)="onStepClick(step.id)">
      <span class="stage-name">{{ step.header }}</span>
      <div class="step-item-actions" (click)="$event.stopPropagation()">
        <button type="button" class="step-menu-btn" [matMenuTriggerFor]="readyToGoMenu">
          <mat-icon>more_horiz</mat-icon>
        </button>
        <mat-menu #readyToGoMenu="matMenu" class="step-reset-menu">
          <button mat-menu-item (click)="onResetStep(step)">
            <mat-icon>refresh</mat-icon>
            <span>{{ 'sidebarProcessSteps.resetStepTitle' | translate }}</span>
          </button>
        </mat-menu>
        @if (step.status === statusEnum.COMPLETED) {
          <mat-icon class="stage-check-icon">check_circle</mat-icon>
        }
      </div>
    </div>
  }

  @if (finalStage; as stage) {
    <ng-container *ngTemplateOutlet="stageBlock; context: { $implicit: stage, idx: mergedStages.length - 1 }"></ng-container>
  }

  <!-- B2B MyQuest only; off while a workplan is generating, so it can't land on the fresh process -->
  @if (!isB2C && newQuestStageNames.length > 0) {
    <div class="new-quest-zone">
      <button type="button" class="new-quest-button" cdkOverlayOrigin #newQuestOrigin="cdkOverlayOrigin"
              [disabled]="isGeneratingArtifact" aria-describedby="new-quest-card"
              (mouseenter)="onNewQuestButtonMouseEnter()" (mouseleave)="hideNewQuestCard()"
              (focus)="onNewQuestButtonFocus($event)" (blur)="hideNewQuestCard()"
              (click)="onResetProcess()">
        <span class="new-quest-ring">
          <ng-container *ngTemplateOutlet="newQuestIcon; context: { size: 36 }"></ng-container>
        </span>
        <span class="new-quest-label">{{ 'sidebarProcessSteps.resetProcessTitle' | translate }}</span>
      </button>
    </div>

    <ng-template cdkConnectedOverlay
                 [cdkConnectedOverlayOrigin]="newQuestOrigin"
                 [cdkConnectedOverlayOpen]="isNewQuestCardOpen && !isGeneratingArtifact"
                 [cdkConnectedOverlayPositions]="newQuestCardPositions"
                 [cdkConnectedOverlayPush]="true"
                 [cdkConnectedOverlayViewportMargin]="8"
                 (positionChange)="onNewQuestCardPositionChange($event)"
                 (overlayKeydown)="onNewQuestCardKeydown($event)">
      <div id="new-quest-card" class="new-quest-card" [class.below]="isNewQuestCardBelow" role="tooltip">
        <div class="new-quest-card-title">
          <ng-container *ngTemplateOutlet="newQuestIcon; context: { size: 16 }"></ng-container>
          {{ 'sidebarProcessSteps.newQuestCardTitle' | translate }}
        </div>
        <div [innerHTML]="'sidebarProcessSteps.newQuestCardMessage' | translate: { stage: newQuestStageNames[0] }"></div>
        <div class="new-quest-card-flow">
          @for (name of newQuestStageNames; track $index; let first = $first) {
            @if (!first) {
              <span class="new-quest-card-arrow">{{ isRTL ? '←' : '→' }}</span>
            }
            <span class="new-quest-card-chip" [class.start]="first">{{ name }}</span>
          }
        </div>
        <div class="new-quest-card-kept">✓ {{ 'sidebarProcessSteps.newQuestCardKept' | translate }}</div>
      </div>
    </ng-template>
  }
</div>

<ng-template #stageBlock let-stage let-idx="idx">
  <div class="stage-block">
    <div class="stage-header"
      [class.active]="stage.id === currentStageId"
      [class.completed]="stage.status === statusEnum.COMPLETED"
      [class.in-progress]="stage.status === statusEnum.IN_PROGRESS"
      (click)="onStageClick(stage.id)">
      <span class="stage-name">{{ stage.name }}</span>
      @if (stage.status === statusEnum.COMPLETED) {
        <mat-icon class="stage-check-icon">check_circle</mat-icon>
      }
      <mat-icon class="stage-toggle-icon">{{ expandedStageId === stage.id ? 'expand_less' : 'expand_more' }}</mat-icon>
    </div>

    @if (expandedStageId === stage.id) {
      <div class="steps-list">
        @for (step of stage.steps; track step.id) {
          <div class="step-item"
            [class.active]="step.id === currentStepId && !isOnPersonalView"
            [class.completed]="step.status === statusEnum.COMPLETED"
            (click)="onStepClick(step.id)">
            <span class="step-name">{{ step.header }}</span>
            <div class="step-item-actions" (click)="$event.stopPropagation()">
              @if (idx > 0 || (step.stepType === stepTypeEnum.CHAT && step.idx > 1)) {
                <button type="button" class="step-menu-btn" [matMenuTriggerFor]="stepMenu">
                  <mat-icon>more_horiz</mat-icon>
                </button>
                <mat-menu #stepMenu="matMenu" class="step-reset-menu">
                  <button mat-menu-item (click)="onResetStep(step)">
                    <mat-icon>refresh</mat-icon>
                    <span>{{ 'sidebarProcessSteps.resetStepTitle' | translate }}</span>
                  </button>
                </mat-menu>
              }
              @if (step.status === statusEnum.COMPLETED) {
                <mat-icon class="step-check-icon">check</mat-icon>
              }
            </div>
          </div>
          @if (step.stepType === stepTypeEnum.INTRO_CONSENT && step.status === statusEnum.COMPLETED) {
            <div class="step-item my-drivers" [class.active]="isOnPersonalView" (click)="onMyDriversClick()">
              <mat-icon class="my-drivers-icon">psychology_alt</mat-icon>
              <span class="step-name">{{ 'nav.myPersonalView' | translate }}</span>
            </div>
          }
          @if (idx === 0 && showCVUpload && cvUploadAfterStepId && step.id === cvUploadAfterStepId) {
            <div class="step-item cv-upload" (click)="onCVUploadClick()">
              <mat-icon class="cv-upload-icon">upload_file</mat-icon>
              <span class="step-name">{{ 'structuredProcessView.uploadCVSidebar' | translate }}</span>
            </div>
          }
        }
      </div>
    }
  </div>
</ng-template>

<!-- the counter-clockwise arrow of "Start a New Quest" -->
<ng-template #newQuestIcon let-size="size">
  <svg class="new-quest-icon" [attr.width]="size" [attr.height]="size" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M3 12a9 9 0 1 0 3-6.7" />
    <path d="M3 4v5h5" />
  </svg>
</ng-template>
